import { zodResolver } from "@hookform/resolvers/zod";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, Stack, Typography } from "@mui/material";
import { BlTextButton } from "Components/Shared/Buttons/BlTextButton";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { StyledValidationText } from "Components/Shared/FormStyles";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { ImageUploadField } from "Components/Stories/ImageUploadField";
import { StoryFormatPicker } from "Components/Stories/Management/StoryFormatPicker";
import { StoryTextCounter } from "Components/Stories/Management/StoryTextCounter";
import {
  StoryFormStep,
  StoryFormStepIndent,
} from "Components/Stories/Management/StoryFormStep";
import {
  type StoryFormat,
  getStoryFormat,
} from "Components/Stories/Management/storyFormat";
import {
  MAX_IMAGE_FILE_SIZE_MB,
  MAX_VIDEO_DURATION_SECONDS,
  MAX_VIDEO_FILE_SIZE_MB,
} from "Components/Stories/storyMediaLimits";
import { VideoUploadField } from "Components/Stories/VideoUploadField";
import { type ReactNode, useState } from "react";
import { Controller, type FieldErrors, useForm } from "react-hook-form";
import {
  getStoryFormSchema,
  MAX_TEXT_LENGTH,
  OFFER_FIELDS,
  type StoryFormData,
} from "Schemas/Stories/storyFormSchema";
import { Resources, useResource } from "Translations/Resources";

const FIELD_RESOURCES = Resources.Stories.PostStory;
const FORM_RESOURCES = Resources.Stories.PostStory.Form;

/** The offer step owns the sheet and the story button that opens it. */
const OFFER_STEP_FIELDS = [
  ...OFFER_FIELDS,
  "buttonLabel",
] as const satisfies ReadonlyArray<keyof StoryFormData>;

const hasOfferValues = (data: StoryFormData) =>
  OFFER_STEP_FIELDS.some((field) => data[field].trim() !== "");

const hasOfferErrors = (errors: FieldErrors<StoryFormData>) =>
  OFFER_STEP_FIELDS.some((field) => errors[field] !== undefined);

type Props = {
  defaultValues: StoryFormData;
  isPending: boolean;
  /** The primary action (type=submit). */
  submitLabel: string;
  isSubmitDisabled?: boolean;
  onSubmit: (data: StoryFormData) => void;
  /** Optional secondary action, validated the same way (e.g. "save as draft"). */
  secondaryLabel?: string;
  onSecondarySubmit?: (data: StoryFormData) => void;
  /** Alerts/usage rendered above the buttons — owned by the page, not the form. */
  footer?: ReactNode;
};

/** The story content form shared by create and edit; the page decides what submitting means. */
export const StoryForm = ({
  defaultValues,
  isPending,
  submitLabel,
  isSubmitDisabled = false,
  onSubmit,
  secondaryLabel,
  onSecondarySubmit,
  footer,
}: Props) => {
  const { t } = useResource();

  // Format and "offer attached" are UI-only: the data model carries just the URLs and sheet fields.
  const [format, setFormat] = useState<StoryFormat>(() =>
    getStoryFormat(defaultValues),
  );
  const [isOfferAttached, setIsOfferAttached] = useState(() =>
    hasOfferValues(defaultValues),
  );

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<StoryFormData>({
    resolver: zodResolver(getStoryFormSchema(t)),
    defaultValues,
  });

  // Switching format starts the media step empty — nothing is carried over between the slots.
  const changeFormat = (next: StoryFormat) => {
    if (next === format) {
      return;
    }
    setFormat(next);
    setValue("imageUrl", "");
    setValue("videoUrl", "");
    clearErrors(["imageUrl", "videoUrl"]);
  };

  const removeOffer = () => {
    for (const field of OFFER_STEP_FIELDS) {
      setValue(field, "");
    }
    clearErrors(OFFER_STEP_FIELDS);
    setIsOfferAttached(false);
  };

  // A collapsed offer would hide its validation messages, so open it when they appear.
  const revealOfferErrors = (invalidErrors: FieldErrors<StoryFormData>) => {
    if (hasOfferErrors(invalidErrors)) {
      setIsOfferAttached(true);
    }
  };

  // The schema only knows "at least one medium"; the format is form state, so a video story with
  // just a poster would otherwise save as a photo story. Guard it here, after schema validation.
  const submitWith = (handler: (data: StoryFormData) => void) =>
    handleSubmit((data) => {
      if (format === "video" && data.videoUrl.trim() === "") {
        setError("videoUrl", {
          message: t(FIELD_RESOURCES.Validation.MediaRequired),
        });
        return;
      }
      handler(data);
    }, revealOfferErrors);

  // The schema reports "no medium" on imageUrl; for a video story that belongs under the video slot.
  const videoRequiredMessage =
    errors.videoUrl?.message ?? errors.imageUrl?.message;

  return (
    <Box component="form" onSubmit={submitWith(onSubmit)}>
      <StoryFormStep
        number={1}
        title={t(FORM_RESOURCES.Format.Title)}
        subtitle={t(FORM_RESOURCES.Format.Subtitle)}
      >
        <StoryFormatPicker value={format} onChange={changeFormat} />
      </StoryFormStep>

      {format === "photo" ? (
        <StoryFormStep
          number={2}
          title={t(FORM_RESOURCES.Media.PhotoTitle)}
          subtitle={t(FORM_RESOURCES.Media.PhotoSubtitle, {
            megabytes: MAX_IMAGE_FILE_SIZE_MB,
          })}
        >
          <Controller
            control={control}
            name="imageUrl"
            render={({ field }) => (
              <div>
                <ImageUploadField
                  label={t(FIELD_RESOURCES.StoryImage)}
                  value={field.value}
                  onChange={field.onChange}
                />
                <StyledValidationText>
                  {errors.imageUrl?.message}
                </StyledValidationText>
              </div>
            )}
          />
        </StoryFormStep>
      ) : (
        <StoryFormStep
          number={2}
          title={t(FORM_RESOURCES.Media.VideoTitle)}
          subtitle={t(FORM_RESOURCES.Media.VideoSubtitle, {
            seconds: MAX_VIDEO_DURATION_SECONDS,
            megabytes: MAX_VIDEO_FILE_SIZE_MB,
          })}
        >
          <Controller
            control={control}
            name="videoUrl"
            render={({ field }) => (
              <div>
                <VideoUploadField
                  label={t(FIELD_RESOURCES.StoryVideo)}
                  value={field.value}
                  onChange={(url) => {
                    // The video-required error is set manually on submit, so clear it by hand too.
                    // The schema's "no medium" error sits on imageUrl (shown under this slot via
                    // videoRequiredMessage) and a change only re-validates videoUrl, so clear both.
                    clearErrors(["videoUrl", "imageUrl"]);
                    field.onChange(url);
                  }}
                />
                <StyledValidationText>
                  {videoRequiredMessage}
                </StyledValidationText>
              </div>
            )}
          />
          <Controller
            control={control}
            name="imageUrl"
            render={({ field }) => (
              <ImageUploadField
                label={t(FORM_RESOURCES.Media.PosterLabel)}
                hint={t(FORM_RESOURCES.Media.PosterHint)}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </StoryFormStep>
      )}

      <StoryFormStep number={3} title={t(FORM_RESOURCES.Text.Title)} isOptional>
        <FormInput
          control={control}
          name="text"
          disablePadding
          type="textarea"
          rows={3}
          // slotProps replaces TextInput's own (which carries maxLength), so the cap rides along here.
          slotProps={{
            htmlInput: {
              "aria-label": t(FIELD_RESOURCES.Text),
              maxLength: MAX_TEXT_LENGTH,
            },
          }}
          helperText={<StoryTextCounter control={control} />}
        />
      </StoryFormStep>

      <StoryFormStep
        number={4}
        title={t(FIELD_RESOURCES.SheetSection)}
        subtitle={t(FORM_RESOURCES.Offer.Subtitle)}
        isOptional
        isLast
      >
        {isOfferAttached ? (
          <Stack component={PrimaryPaper} gap={2}>
            <div>
              <FormInput
                control={control}
                name="sheetTitle"
                label={t(FIELD_RESOURCES.SheetTitle)}
              />
              <FormInput
                control={control}
                name="sheetDescription"
                label={t(FIELD_RESOURCES.SheetDescription)}
                type="textarea"
                rows={3}
              />
              <Box
                display="grid"
                gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                columnGap={2}
              >
                <FormInput
                  control={control}
                  name="sheetPrice"
                  label={t(FIELD_RESOURCES.SheetPrice)}
                />
                <FormInput
                  control={control}
                  name="sheetPriceNote"
                  label={t(FIELD_RESOURCES.SheetPriceNote)}
                />
              </Box>
            </div>
            <Controller
              control={control}
              name="sheetImageUrl"
              render={({ field }) => (
                <ImageUploadField
                  label={t(FIELD_RESOURCES.SheetImage)}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <div>
              <Typography fontWeight={600}>
                {t(FORM_RESOURCES.Button.Title)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(FORM_RESOURCES.Button.Subtitle)}
              </Typography>
            </div>
            <Box
              display="grid"
              gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
              columnGap={2}
            >
              <FormInput
                control={control}
                name="buttonLabel"
                label={t(FIELD_RESOURCES.ButtonLabel)}
                placeholder={t(FORM_RESOURCES.Button.LabelPlaceholder)}
              />
              <FormInput
                control={control}
                name="buttonUrl"
                label={t(FIELD_RESOURCES.ButtonUrl)}
                placeholder={t(FORM_RESOURCES.Button.UrlPlaceholder)}
              />
            </Box>
            <Box alignSelf="flex-start">
              <BlTextButton
                color="error"
                startIcon={<DeleteIcon />}
                onClick={removeOffer}
              >
                {t(FORM_RESOURCES.Offer.Remove)}
              </BlTextButton>
            </Box>
          </Stack>
        ) : (
          <div>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setIsOfferAttached(true)}
            >
              {t(FORM_RESOURCES.Offer.Attach)}
            </Button>
          </div>
        )}
      </StoryFormStep>

      <StoryFormStepIndent>
        <Stack gap={3} mt={5}>
          {footer}

          <Stack direction="row" gap={2} flexWrap="wrap">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              loading={isPending}
              disabled={isSubmitDisabled}
            >
              {submitLabel}
            </Button>
            {secondaryLabel && onSecondarySubmit && (
              <Button
                type="button"
                variant="text"
                size="large"
                color="primary"
                disabled={isPending}
                onClick={submitWith(onSecondarySubmit)}
              >
                {secondaryLabel}
              </Button>
            )}
          </Stack>
        </Stack>
      </StoryFormStepIndent>
    </Box>
  );
};
